import React from 'react';
import { motion } from 'framer-motion';
import { Progress } from './progress';
import { cn } from '../../lib/utils';

/**
 * Animated progress bar with percentage display
 */
export const AnimatedProgress = ({ 
  value, 
  showPercentage = true, 
  className,
  size = 'default',
  color = 'primary'
}) => {
  const sizeClasses = {
    sm: 'h-1',
    default: 'h-2',
    lg: 'h-3',
    xl: 'h-4'
  };

  const colorClasses = {
    primary: 'bg-[#0047FF]',
    success: 'bg-[#10B981]',
    warning: 'bg-[#F59E0B]',
    error: 'bg-[#EF4444]'
  };

  return (
    <div className={cn('w-full', className)}>
      <div className="relative">
        <div className={cn('w-full bg-[#E5E7EB] dark:bg-gray-700 rounded-full overflow-hidden', sizeClasses[size])}>
          <motion.div
            className={cn('h-full rounded-full', colorClasses[color])}
            initial={{ width: 0 }}
            animate={{ width: `${value}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
        {showPercentage && (
          <motion.span
            className="absolute -top-6 text-sm font-medium text-[#52525B] dark:text-gray-300"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, left: `calc(${Math.min(value, 95)}% - 10px)` }}
            transition={{ duration: 0.3 }}
          >
            {Math.round(value)}%
          </motion.span>
        )}
      </div>
    </div>
  );
};

/**
 * Step progress indicator for multi-step forms
 */
export const StepProgress = ({ 
  currentStep, 
  totalSteps, 
  labels = [],
  className 
}) => {
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className={cn('w-full', className)}>
      {/* Progress bar */}
      <div className="relative mb-8">
        <div className="h-1 bg-[#E5E7EB] dark:bg-gray-700 rounded-full">
          <motion.div
            className="h-full bg-[#0047FF] rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        
        {/* Step indicators */}
        <div className="absolute top-1/2 -translate-y-1/2 w-full flex justify-between">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
            <motion.div
              key={step}
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors border-2',
                step < currentStep
                  ? 'bg-[#0047FF] border-[#0047FF] text-white'
                  : step === currentStep
                  ? 'bg-white dark:bg-gray-800 border-[#0047FF] text-[#0047FF]'
                  : 'bg-white dark:bg-gray-800 border-[#E5E7EB] dark:border-gray-600 text-[#A1A1AA]'
              )}
              initial={{ scale: 0.8 }}
              animate={{ scale: step === currentStep ? 1.1 : 1 }}
              transition={{ duration: 0.2 }}
            >
              {step < currentStep ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                step
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Labels */}
      {labels.length > 0 && (
        <div className="flex justify-between text-xs sm:text-sm">
          {labels.map((label, i) => (
            <span
              key={i}
              className={cn(
                'text-center max-w-[80px] sm:max-w-none',
                i + 1 <= currentStep ? 'text-[#0047FF] font-medium' : 'text-[#A1A1AA]'
              )}
            >
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Auto-save indicator
 */
export const AutoSaveIndicator = ({ isSaved, lastSaved, className }) => {
  return (
    <motion.div
      className={cn('flex items-center gap-2 text-xs', className)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {isSaved ? (
        <>
          <div className="w-2 h-2 bg-[#10B981] rounded-full" />
          <span className="text-[#10B981]">
            Guardado {lastSaved && `a las ${lastSaved.toLocaleTimeString()}`}
          </span>
        </>
      ) : (
        <>
          <div className="w-2 h-2 bg-[#F59E0B] rounded-full animate-pulse" />
          <span className="text-[#F59E0B]">Guardando...</span>
        </>
      )}
    </motion.div>
  );
};

export default AnimatedProgress;
